import { useState, useEffect } from 'react';
import { 
  UNISWAP_V3_POOL_COMBINED_QUERY,
  PANCAKESWAP_V3_POOL_COMBINED_QUERY,
  SUSHISWAP_V3_POOL_COMBINED_QUERY,
  UNISWAP_V4_POOL_COMBINED_QUERY
} from '../queries/poolQueries';
import { PoolSnapshot, PoolMetadata, Token, PoolDataHookResult } from '../types/pool';
import { getUrlForDEX } from '../services/graphqlClient';

// ============================================================================
// DATA CONVERSION FUNCTIONS (matching your Python logic exactly)
// ============================================================================

// Build a synthetic "today" PoolSnapshot from the live pool entity returned by each
// subgraph. Daily-aggregate tables (Messari liquidityPoolDailySnapshots / native poolDayDatas)
// can lag or skip rows for a given pool while the live entity stays current with every event,
// so this fills the gap between the last daily row and the chain head without modifying any
// historical entry. Returns null when there is no live data, when the last historical row is
// already today's UTC date, or when TVL is zero.
const buildLiveSnapshot = (
  dex: string,
  liveEntity: any,
  historicalSnapshots: PoolSnapshot[],
  token0Decimals: number,
  token1Decimals: number
): PoolSnapshot | null => {
  if (!liveEntity) return null;

  const now = new Date();
  const todayMidnightMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const todayMidnightSec = Math.floor(todayMidnightMs / 1000);

  const last = historicalSnapshots[historicalSnapshots.length - 1];
  if (last) {
    const lastDate = new Date(last.date);
    const lastMidnightMs = Date.UTC(
      lastDate.getUTCFullYear(),
      lastDate.getUTCMonth(),
      lastDate.getUTCDate()
    );
    if (lastMidnightMs >= todayMidnightMs) return null;
  }

  let tvl_usd = 0;
  let token_balances_raw: number[] = [0, 0];
  let token_balances_usd: number[] = [0, 0];
  let cumulative_volume_usd = 0;

  if (dex === 'uniswap_v3') {
    // Messari schema
    tvl_usd = parseFloat(liveEntity.totalValueLockedUSD || '0');
    const balances = liveEntity.inputTokenBalances || ['0', '0'];
    const balancesUSD = liveEntity.inputTokenBalancesUSD || ['0', '0'];
    token_balances_raw = [parseFloat(balances[0] || '0'), parseFloat(balances[1] || '0')];
    token_balances_usd = [parseFloat(balancesUSD[0] || '0'), parseFloat(balancesUSD[1] || '0')];
    cumulative_volume_usd = parseFloat(liveEntity.cumulativeVolumeUSD || '0');
  } else {
    // Native Uniswap V3 schema (Pancake/Sushi/V4): TVL in tokens is human-readable
    tvl_usd = parseFloat(liveEntity.totalValueLockedUSD || '0');
    const tvlToken0 = parseFloat(liveEntity.totalValueLockedToken0 || '0');
    const tvlToken1 = parseFloat(liveEntity.totalValueLockedToken1 || '0');
    token_balances_raw = [
      tvlToken0 * Math.pow(10, token0Decimals),
      tvlToken1 * Math.pow(10, token1Decimals),
    ];
    cumulative_volume_usd = parseFloat(liveEntity.volumeUSD || '0');
  }

  if (tvl_usd <= 0) return null;

  // Today's running daily volume is only derivable when the historical snapshots carry a
  // comparable cumulative figure. Messari's liquidityPoolDailySnapshots exposes
  // cumulativeVolumeUSD per row (so live − last is exact); native-schema poolDayDatas only
  // expose per-day volume, leaving cumulative_volume_usd as 0 on each historical snapshot.
  // In the native case `live.volumeUSD − 0` would equal pool-lifetime volume, not today's,
  // so we leave daily_volume_usd at 0 rather than report a misleading number.
  const lastCumVol = last?.cumulative_volume_usd || 0;
  const daily_volume_usd = dex === 'uniswap_v3' && cumulative_volume_usd > lastCumVol
    ? cumulative_volume_usd - lastCumVol
    : 0;

  return {
    timestamp: todayMidnightSec,
    date: new Date(todayMidnightMs).toISOString(),
    total_value_locked_usd: tvl_usd,
    daily_volume_usd,
    cumulative_volume_usd,
    token_balances_raw,
    token_balances_usd,
    token0_balance_raw: token_balances_raw[0] || null,
    token1_balance_raw: token_balances_raw[1] || null,
    token0_balance_usd: token_balances_usd[0] || null,
    token1_balance_usd: token_balances_usd[1] || null,
    fees_usd: null,
    protocol_fees_usd: null,
  };
};

const convertToPoolSnapshot = (data: any, dex: string): PoolSnapshot | null => {
  try {
    // Handle different timestamp formats for different DEXes
    const timestamp = dex === 'pancakeswap_v3' || dex === 'uniswap_v4' || dex === 'sushiswap_v3'
      ? parseInt(data.date) 
      : parseInt(data.timestamp);
    
    const date = new Date(timestamp * 1000).toISOString();
    
    // Convert numeric fields (matching your Python conversion)
    const tvl_usd = parseFloat(data.totalValueLockedUSD || data.tvlUSD || '0');
    const daily_volume_usd = parseFloat(data.dailyVolumeUSD || data.volumeUSD || '0');
    const cumulative_volume_usd = parseFloat(data.cumulativeVolumeUSD || '0');
    
    // Parse token balances (matching your Python logic)
    const token_balances_raw = data.inputTokenBalances || [0, 0];
    const token_balances_usd = data.inputTokenBalancesUSD || [0, 0];
    
    // Extract individual balances (matching your Python logic)
    const token0_balance_raw = token_balances_raw[0] ? parseFloat(token_balances_raw[0]) : null;
    const token1_balance_raw = token_balances_raw[1] ? parseFloat(token_balances_raw[1]) : null;
    const token0_balance_usd = token_balances_usd[0] ? parseFloat(token_balances_usd[0]) : null;
    const token1_balance_usd = token_balances_usd[1] ? parseFloat(token_balances_usd[1]) : null;
    
    // Parse revenue/fee data (matching your Python logic)
    const daily_protocol_side_revenue = data.dailyProtocolSideRevenueUSD ? 
      parseFloat(data.dailyProtocolSideRevenueUSD) : null;
    const daily_total_revenue = data.dailyTotalRevenueUSD ? 
      parseFloat(data.dailyTotalRevenueUSD) : null;
    
    // For PancakeSwap, SushiSwap, and Uniswap V4, use different field names
    const fees_usd = data.feesUSD ? parseFloat(data.feesUSD) : daily_total_revenue;
    const protocol_fees_usd = data.protocolFeesUSD ? 
      parseFloat(data.protocolFeesUSD) : daily_protocol_side_revenue;
    
    return {
      timestamp,
      date,
      total_value_locked_usd: tvl_usd,
      daily_volume_usd,
      cumulative_volume_usd,
      token_balances_raw: [token0_balance_raw || 0, token1_balance_raw || 0],
      token_balances_usd: [token0_balance_usd || 0, token1_balance_usd || 0],
      token0_balance_raw,
      token1_balance_raw,
      token0_balance_usd,
      token1_balance_usd,
      fees_usd,
      protocol_fees_usd
    };
  } catch (error) {
    console.error('Error converting to PoolSnapshot:', error);
    return null;
  }
};

const convertToPoolMetadata = (data: any, poolName: string, dex: string): PoolMetadata | null => {
  try {
    // Handle null/undefined data
    if (!data) {
      console.warn(`No metadata data for ${poolName} (${dex})`);
      return null;
    }
    
    let tokens: Token[] = [];
    
    if (dex === 'uniswap_v3') {
      // Uniswap V3 uses inputTokens array
      const inputTokens = data.liquidityPool?.inputTokens || [];
      tokens = inputTokens.slice(0, 2).map((t: any) => ({
        id: t.id,
        symbol: t.symbol,
        name: t.name,
        decimals: parseInt(t.decimals)
      }));
    } else if (dex === 'pancakeswap_v3' || dex === 'uniswap_v4' || dex === 'sushiswap_v3') {
      // PancakeSwap V3, Uniswap V4, and SushiSwap V3 use token0/token1 structure
      const pool = data.pool;
      if (pool?.token0) {
        tokens.push({
          id: pool.token0.id || `token0_${pool.id}`,
          symbol: pool.token0.symbol,
          name: pool.token0.name || pool.token0.symbol,
          decimals: parseInt(pool.token0.decimals)
        });
      }
      if (pool?.token1) {
        tokens.push({
          id: pool.token1.id || `token1_${pool.id}`,
          symbol: pool.token1.symbol,
          name: pool.token1.name || pool.token1.symbol,
          decimals: parseInt(pool.token1.decimals)
        });
      }
    }
    
    // Ensure we have at least 2 tokens (matching your Python fallback logic)
    while (tokens.length < 2) {
      tokens.push({
        id: `unknown${tokens.length}`,
        symbol: `Token${tokens.length}`,
        name: `Token ${tokens.length}`,
        decimals: 18
      });
    }
    
    return {
      address: data.liquidityPool?.id || data.pool?.id || '',
      name: poolName,
      dex,
      tokens: tokens.slice(0, 2),
      token0_symbol: tokens[0]?.symbol,
      token1_symbol: tokens[1]?.symbol,
      token0_decimals: tokens[0]?.decimals,
      token1_decimals: tokens[1]?.decimals
    };
  } catch (error) {
    console.error('Error converting to PoolMetadata:', error);
    return null;
  }
};

// ============================================================================
// CUSTOM HOOKS USING FETCH (matching Python approach)
// ============================================================================

// July 1, 2025 timestamp (00:00:00 UTC)
const JULY_1_2025_TIMESTAMP = Math.floor(new Date('2025-07-01T00:00:00Z').getTime() / 1000);

const useGraphQLQuery = (query: string, variables: any, dex: string, enablePolling: boolean = true, enabled: boolean = true) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(enabled); // Only show loading if enabled
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Don't make any queries if disabled
    if (!enabled) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const url = getUrlForDEX(dex);
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_THE_GRAPH_API_KEY}`
          },
          body: JSON.stringify({
            query,
            variables
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
        }

        setData(result.data);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchData();

    // Set up polling if enabled
    let intervalId: NodeJS.Timeout | null = null;
    if (enablePolling) {
      // Default to 1 hour (3600000 ms), configurable via environment variable
      const intervalMs = process.env.REACT_APP_UPDATE_INTERVAL 
        ? parseInt(process.env.REACT_APP_UPDATE_INTERVAL, 10) 
        : 60 * 60 * 1000; // 1 hour
      intervalId = setInterval(fetchData, intervalMs);
    }

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [query, JSON.stringify(variables), dex, enablePolling, enabled]);

  return { data, loading, error };
};

export const useUniswapV3PoolData = (poolAddress: string, poolName: string, days: number = 30, enabled: boolean = true): PoolDataHookResult => {
  const combinedResult = useGraphQLQuery(
    UNISWAP_V3_POOL_COMBINED_QUERY,
    { pool: poolAddress, first: days, skip: 0, dateGte: JULY_1_2025_TIMESTAMP },
    'uniswap_v3',
    true, // Enable polling every 1 hour
    enabled // Only make queries if enabled
  );

  const snapshots: PoolSnapshot[] = combinedResult.data?.liquidityPoolDailySnapshots?.map((data: any) =>
    convertToPoolSnapshot(data, 'uniswap_v3')
  ).filter((s: PoolSnapshot | null): s is PoolSnapshot => s !== null).reverse() || []; // Reverse to get chronological order

  const metadata = convertToPoolMetadata(combinedResult.data, poolName, 'uniswap_v3');

  const liveSnapshot = buildLiveSnapshot(
    'uniswap_v3',
    combinedResult.data?.liquidityPool,
    snapshots,
    metadata?.token0_decimals ?? 18,
    metadata?.token1_decimals ?? 18
  );
  if (liveSnapshot) snapshots.push(liveSnapshot);

  return {
    snapshots,
    metadata,
    loading: combinedResult.loading,
    error: combinedResult.error
  };
};

export const usePancakeSwapV3PoolData = (poolAddress: string, poolName: string, days: number = 30, enabled: boolean = true): PoolDataHookResult => {
  const combinedResult = useGraphQLQuery(
    PANCAKESWAP_V3_POOL_COMBINED_QUERY,
    { pool: poolAddress.toLowerCase(), first: days, skip: 0, dateGte: JULY_1_2025_TIMESTAMP },
    'pancakeswap_v3',
    true, // Enable polling every 1 hour
    enabled // Only make queries if enabled
  );

  const snapshots: PoolSnapshot[] = combinedResult.data?.poolDayDatas?.map((data: any) =>
    convertToPoolSnapshot(data, 'pancakeswap_v3')
  ).filter((s: PoolSnapshot | null): s is PoolSnapshot => s !== null).reverse() || []; // Reverse to get chronological order

  const metadata = convertToPoolMetadata(combinedResult.data, poolName, 'pancakeswap_v3');

  const liveSnapshot = buildLiveSnapshot(
    'pancakeswap_v3',
    combinedResult.data?.pool,
    snapshots,
    metadata?.token0_decimals ?? 18,
    metadata?.token1_decimals ?? 18
  );
  if (liveSnapshot) snapshots.push(liveSnapshot);

  return {
    snapshots,
    metadata,
    loading: combinedResult.loading,
    error: combinedResult.error
  };
};

export const useSushiSwapV3PoolData = (poolAddress: string, poolName: string, days: number = 30, enabled: boolean = true): PoolDataHookResult => {
  const combinedResult = useGraphQLQuery(
    SUSHISWAP_V3_POOL_COMBINED_QUERY,
    { pool: poolAddress.toLowerCase(), first: days, dateGte: JULY_1_2025_TIMESTAMP },
    'sushiswap_v3',
    true, // Enable polling every 1 hour
    enabled // Only make queries if enabled
  );

  const snapshots: PoolSnapshot[] = combinedResult.data?.poolDayDatas?.map((data: any) =>
    convertToPoolSnapshot(data, 'sushiswap_v3')
  ).filter((s: PoolSnapshot | null): s is PoolSnapshot => s !== null) || []; // SushiSwap returns data in chronological order (asc)

  const metadata = convertToPoolMetadata(combinedResult.data, poolName, 'sushiswap_v3');

  const liveSnapshot = buildLiveSnapshot(
    'sushiswap_v3',
    combinedResult.data?.pool,
    snapshots,
    metadata?.token0_decimals ?? 18,
    metadata?.token1_decimals ?? 18
  );
  if (liveSnapshot) snapshots.push(liveSnapshot);

  return {
    snapshots,
    metadata,
    loading: combinedResult.loading,
    error: combinedResult.error
  };
};

export const useUniswapV4PoolData = (poolAddress: string, poolName: string, days: number = 30, enabled: boolean = true): PoolDataHookResult => {
  const combinedResult = useGraphQLQuery(
    UNISWAP_V4_POOL_COMBINED_QUERY,
    { pool: poolAddress, first: days, skip: 0, dateGte: JULY_1_2025_TIMESTAMP },
    'uniswap_v4',
    true, // Enable polling every 1 hour
    enabled // Only make queries if enabled
  );

  const snapshots: PoolSnapshot[] = combinedResult.data?.poolDayDatas?.map((data: any) =>
    convertToPoolSnapshot(data, 'uniswap_v4')
  ).filter((s: PoolSnapshot | null): s is PoolSnapshot => s !== null).reverse() || []; // Reverse to get chronological order

  const metadata = convertToPoolMetadata(combinedResult.data, poolName, 'uniswap_v4');

  const liveSnapshot = buildLiveSnapshot(
    'uniswap_v4',
    combinedResult.data?.pool,
    snapshots,
    metadata?.token0_decimals ?? 18,
    metadata?.token1_decimals ?? 18
  );
  if (liveSnapshot) snapshots.push(liveSnapshot);

  return {
    snapshots,
    metadata,
    loading: combinedResult.loading,
    error: combinedResult.error
  };
};

// ============================================================================
// UNIFIED HOOK
// ============================================================================

export const usePoolData = (poolAddress: string, poolName: string, dex: string, days: number = 30): PoolDataHookResult => {
  // Always call all hooks to avoid conditional hook calls (React Rules of Hooks)
  // But optimize by only making queries for the relevant DEX
  const uniswapV3Result = useUniswapV3PoolData(poolAddress, poolName, days, dex === 'uniswap_v3');
  const pancakeswapV3Result = usePancakeSwapV3PoolData(poolAddress, poolName, days, dex === 'pancakeswap_v3');
  const sushiswapV3Result = useSushiSwapV3PoolData(poolAddress, poolName, days, dex === 'sushiswap_v3');
  const uniswapV4Result = useUniswapV4PoolData(poolAddress, poolName, days, dex === 'uniswap_v4');

  // Return the appropriate result based on DEX
  switch (dex) {
    case 'uniswap_v3':
      return uniswapV3Result;
    case 'pancakeswap_v3':
      return pancakeswapV3Result;
    case 'sushiswap_v3':
      return sushiswapV3Result;
    case 'uniswap_v4':
      return uniswapV4Result;
    default:
      return {
        snapshots: [],
        metadata: null,
        loading: false,
        error: new Error(`Unknown DEX: ${dex}`)
      };
  }
};